import { useEffect, useRef } from 'react';
import { usePreferences } from '@/hooks/usePreferences';
import { usePrefsStore } from '@/core/stores/prefsStore';
import { useSessionStore } from '@/core/stores/sessionStore';
import type { UserPreferences } from '@/core/models/preferences';
import { useToast } from '@/ui/Toast';
import { ALL_APPEARANCE_KEYS, appearanceValues } from './appearanceCategories';

const account = () =>
  useSessionStore.getState().accounts.find((a) => a.useThisAsActive)?.handle ?? 'guest';

export function useAppearanceReset() {
  const { updatePreferences } = usePreferences();
  const toast = useToast();
  const pending = useRef<(() => void) | null>(null);
  useEffect(() => () => pending.current?.(), []);

  return (keys: readonly (keyof UserPreferences)[], label: string) => {
    pending.current?.();
    const before = appearanceValues(keys, usePrefsStore.getState().prefs);
    const owner = account();
    void updatePreferences(appearanceValues(keys));
    let valid = true;
    const stopPrefs = usePrefsStore.subscribe((next, prev) => {
      if (ALL_APPEARANCE_KEYS.some((key) => next.prefs[key] !== prev.prefs[key])) cancel();
    });
    const stopSession = useSessionStore.subscribe(() => {
      if (account() !== owner) cancel();
    });
    function cancel() {
      if (!valid) return;
      valid = false;
      stopPrefs();
      stopSession();
      pending.current = null;
      dismiss?.();
    }
    pending.current = cancel;
    const dismiss = toast(
      `${label} reset`,
      {
        label: 'Undo',
        onClick: () => {
          if (!valid || account() !== owner) return;
          cancel();
          void updatePreferences(before);
        },
      },
      { durationMs: 8000, pauseOnInteraction: true, onDismiss: cancel },
    );
  };
}
